import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTutorial } from './TutorialProvider';
import { TutorialSpotlight } from './TutorialSpotlight';
import { TutorialMascot } from './TutorialMascot';
import { DemoStickerStage, DEMO_TARGET_ID } from './DemoStickerStage';

const NAVIGATE_DELAY_MS = 1400;
const HIGHLIGHT_TARGET_TIMEOUT_MS = 2500;

export function TutorialOverlay() {
  const { currentStep, state, next, skip, signalWrongAction, getTarget } = useTutorial();
  const navigate = useNavigate();
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [mascotPosition, setMascotPosition] = useState<'top' | 'bottom'>('bottom');

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') skip();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [skip]);

  useEffect(() => {
    if (currentStep.kind === 'navigate') {
      navigate(`/album?tab=${currentStep.tab}`, { replace: false });
      const t = window.setTimeout(() => next(), NAVIGATE_DELAY_MS);
      return () => window.clearTimeout(t);
    }
    if (currentStep.kind === 'highlight') {
      navigate(`/album?tab=${currentStep.tab}`, { replace: false });
    }
    return undefined;
  }, [currentStep, navigate, next]);

  useEffect(() => {
    setTargetEl(null);

    if (currentStep.kind !== 'highlight' && currentStep.kind !== 'demo-sticker') {
      return undefined;
    }

    const targetId =
      currentStep.kind === 'demo-sticker' ? DEMO_TARGET_ID : currentStep.targetId;

    let cancelled = false;
    let raf = 0;
    const startedAt = Date.now();

    const tick = () => {
      if (cancelled) return;
      const el = getTarget(targetId);
      if (el) {
        setTargetEl(el);
        if (currentStep.kind === 'highlight') {
          const rect = el.getBoundingClientRect();
          const targetCenterY = rect.top + rect.height / 2;
          const inUpperHalf = targetCenterY < window.innerHeight / 2;
          const nextPos: 'top' | 'bottom' = inUpperHalf ? 'bottom' : 'top';
          setMascotPosition(nextPos);
          const desiredCenterY =
            nextPos === 'top'
              ? window.innerHeight * 0.7
              : window.innerHeight * 0.32;
          const delta = targetCenterY - desiredCenterY;
          if (Math.abs(delta) > 16) {
            window.scrollBy({ top: delta, behavior: 'smooth' });
          }
        }
        return;
      }
      if (Date.now() - startedAt > HIGHLIGHT_TARGET_TIMEOUT_MS) {
        if (currentStep.kind === 'highlight') {
          next();
        }
        return;
      }
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [currentStep, getTarget, state.targetVersion, next]);

  const handleWrongClick = () => {
    signalWrongAction();
  };

  const renderBlocker = () => {
    if (currentStep.kind === 'demo-sticker' && targetEl) {
      const r = targetEl.getBoundingClientRect();
      const pad = 12;
      return (
        <>
          <div
            onPointerDown={handleWrongClick}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              height: Math.max(0, r.top - pad),
              zIndex: 61,
            }}
          />
          <div
            onPointerDown={handleWrongClick}
            style={{
              position: 'fixed',
              top: r.bottom + pad,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 61,
            }}
          />
          <div
            onPointerDown={handleWrongClick}
            style={{
              position: 'fixed',
              top: Math.max(0, r.top - pad),
              left: 0,
              width: Math.max(0, r.left - pad),
              height: r.height + pad * 2,
              zIndex: 61,
            }}
          />
          <div
            onPointerDown={handleWrongClick}
            style={{
              position: 'fixed',
              top: Math.max(0, r.top - pad),
              left: r.right + pad,
              right: 0,
              height: r.height + pad * 2,
              zIndex: 61,
            }}
          />
        </>
      );
    }

    return (
      <div
        onPointerDown={handleWrongClick}
        className="fixed inset-0"
        style={{ zIndex: 61 }}
      />
    );
  };

  const renderContent = () => {
    if (currentStep.kind === 'intro') {
      return (
        <TutorialMascot
          variant="center"
          message={currentStep.mascot}
          actions={[
            { label: 'Pular', variant: 'ghost', onClick: skip },
            { label: 'Bora!', variant: 'primary', onClick: next },
          ]}
        />
      );
    }

    if (currentStep.kind === 'final') {
      return (
        <TutorialMascot
          variant="center"
          message={currentStep.mascot}
          actions={[{ label: 'Beleza!', variant: 'primary', onClick: skip }]}
        />
      );
    }

    if (currentStep.kind === 'navigate') {
      return <TutorialMascot variant="corner" message={currentStep.mascot} />;
    }

    if (currentStep.kind === 'demo-sticker') {
      return (
        <>
          <DemoStickerStage />
          <TutorialMascot
            variant="corner"
            message={currentStep.mascot}
            hint={currentStep.hint}
            actions={[{ label: 'Pular tutorial', variant: 'ghost', onClick: skip }]}
          />
        </>
      );
    }

    if (currentStep.kind === 'highlight') {
      return (
        <TutorialMascot
          variant="corner"
          position={mascotPosition}
          message={currentStep.mascot}
          actions={[
            { label: 'Pular', variant: 'ghost', onClick: skip },
            { label: 'Próximo', variant: 'primary', onClick: next },
          ]}
        />
      );
    }

    return null;
  };

  return createPortal(
    <>
      <TutorialSpotlight target={targetEl} />
      {renderBlocker()}
      {renderContent()}
    </>,
    document.body
  );
}
